import { DashboardCard } from '@/components/ui/dashboard-card.tsx';
import { cn } from '@/utils/utils.ts';
import style from '@/pages/dashboard/advanced/index.module.scss';
import { format } from 'date-fns';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select.tsx';
import { SelectValue } from '@radix-ui/react-select';
import { DateRangePicker } from '@/components/ui/date-range-picker.tsx';
import {
  RangeGranularity,
  useOverviewQuery,
} from '@/features/dashboard/hooks/queries/get-overview.ts';
import { EnergySummaryCards } from '@/features/dashboard/components/admin/energy-summary-cards.tsx';
import { useMemo, useState } from 'react';
import { EnergyManagementChart } from '@/components/energy-management-chart.tsx';
import PerformanceMonitoringOptions from '@/features/dashboard/components/admin/performance-monitoring-options.tsx';
import { useTranslation } from 'react-i18next';
import { useGetPlantsQuery } from '@/features/plants/hooks/queries/use-get-plants.tsx';
import { LastUpdateTimestamp } from '@/components/last-update-timestamp.tsx';

export const PerformanceMonitoring = ({ className }: { className: string }) => {
  const { t } = useTranslation();
  const [currentViewContext, setCurrentViewContext] = useState('all');
  const [dateRange, setDateRange] = useState<{
    startDate: Date;
    endDate: Date | undefined;
    rangeGranularity: RangeGranularity;
  }>({
    startDate: new Date(new Date().setHours(0, 0, 0, 0)),
    endDate: new Date(new Date().setHours(23, 59, 59, 999)),
    rangeGranularity: '5min',
  });

  const {
    data: overviewData,
    isLoading: isOverviewLoading,
    isFetching,
    dataUpdatedAt: overviewDataUpdatedAt,
  } = useOverviewQuery({
    dateRange,
    viewContext: currentViewContext,
  });

  const { data: plants, isLoading: arePlantsLoading } = useGetPlantsQuery();

  const availableOverview = useMemo(
    () =>
      [{ value: 'all', label: t('plants.overview.allPowerPlants') }].concat(
        plants?.length
          ? plants
              ?.filter((plant) => plant.basicDetails.status !== 'Archived')
              ?.map(({ plantId, basicDetails }) => ({
                value: plantId,
                label: basicDetails.name,
              }))
          : []
      ),
    [plants, t]
  );

  const getPlantReport = () => {
    if (currentViewContext === 'all') return;

    const plant = plants?.find((p) => p.plantId === currentViewContext);
    if (!plant) return;

    const startDate = format(dateRange.startDate, 'MMM d, yyyy');
    const endDate = format(dateRange?.endDate ?? dateRange.startDate, 'MMM d, yyyy');
    const reportDate = startDate === endDate ? startDate : `${startDate} - ${endDate}`;

    const Battery_Charge = overviewData?.overallPerformance.Battery_Charge ?? 0;

    // TODO: Add Available Cycles (cicli reali garantati pentru baterii)
    const Battery_Cycles = Battery_Charge
      ? (Battery_Charge / Number(plant.technicalSpecifications.storageCapacity)).toFixed(2)
      : 0;

    return {
      reportDate,
      plantName: plant.basicDetails.name,
      plantLocation: `${plant?.siteInformation.country.value}, ${plant?.siteInformation.city.value}`,
      energyData: {
        yield: overviewData?.overallPerformance.Yield ?? 0,
        consumed: overviewData?.overallPerformance.Consumed ?? 0,
        consumedFromGrid: overviewData?.overallPerformance.Consumed_from_Grid ?? 0,
        fedToGrid: overviewData?.overallPerformance.Fed_to_Grid ?? 0,
        batteryCharge: overviewData?.overallPerformance.Battery_Charge ?? 0,
        batteryDischarge: overviewData?.overallPerformance.Battery_Discharge ?? 0,
        batteryCycles: Number(Battery_Cycles),
      },
    };
  };

  const isPerformanceMonitoringLoading = isOverviewLoading || arePlantsLoading;

  return (
    <DashboardCard
      isLoading={isPerformanceMonitoringLoading}
      className={cn(
        isFetching && 'pointer-events-none animate-pulse opacity-60',
        'pb-5',
        className
      )}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <div className="flex w-full justify-between">
            <div className="flex flex-col">
              <h3 className="leading font-semibold">
                {t('dashboard.performance_monitoring.title')}
              </h3>
              <span className="text-muted-foreground h-4 text-xs">
                <LastUpdateTimestamp updatedAt={overviewDataUpdatedAt} updateInterval={5000} />
              </span>
            </div>

            <PerformanceMonitoringOptions
              startDate={dateRange.startDate}
              currentViewContext={currentViewContext}
              getPlantReport={getPlantReport}
            />
          </div>
          <div className="flex items-center justify-between gap-2">
            <div className="overflow-hidden p-0.5 text-nowrap sm:w-full sm:max-w-[200px]">
              <Select
                defaultValue={currentViewContext}
                onValueChange={(e) => {
                  setCurrentViewContext(e);
                }}
              >
                <SelectTrigger className="h-9 bg-white">
                  <SelectValue
                    placeholder={t('dashboard.performance_monitoring.select_plant')}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {availableOverview?.map((p) => {
                      return (
                        <SelectItem value={p.value} key={p.value}>
                          <div className="overflow-hidden text-ellipsis">{p.label}</div>
                        </SelectItem>
                      );
                    })}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <div>
              <DateRangePicker
                onUpdate={(values) => {
                  setDateRange((current) => ({
                    ...current,
                    startDate: values.range.from,
                    endDate: values.range.to,
                    rangeGranularity: values.rangeGranularity as RangeGranularity,
                  }));
                }}
                initialDateFrom={dateRange.startDate}
                initialDateTo={dateRange.endDate}
                locale="ro-RO"
                defaultGranularity="5min"
              />
            </div>
          </div>
        </div>
        <div className={cn(style.content_grid)}>
          <EnergySummaryCards
            overallPerformance={overviewData?.overallPerformance}
            className={style.energy_summary_cards}
            rangeGranularity={dateRange.rangeGranularity}
          />
          <EnergyManagementChart
            data={overviewData?.energyManagement ?? []}
            className={style.energy_management}
            rangeGranularity={overviewData?.rangeGranularity ?? dateRange.rangeGranularity}
            currentViewContext={currentViewContext}
          />
        </div>
      </div>
    </DashboardCard>
  );
};
